import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useClientsSupabaseStore } from "@/store/api/clientsSupabaseStore";
import { Client } from "@/types/types";
import { useClientActionsStore } from "@/store/api/clients/clientActionsStore";

interface PauseSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
}

export function PauseSubscriptionDialog({
  open,
  onOpenChange,
  client,
}: PauseSubscriptionDialogProps) {
  const { toast } = useToast();
  const { t } = useTranslation(["subscriptions", "common", "clients"]);
  const { pauseSubscription, resumeSubscription, isLoading } = useClientActionsStore();
  const {fetchClientById}  = useClientsSupabaseStore();
  const isPaused = client.status === "Paused";
  const [pauseStartDate, setPauseStartDate] = useState<Date | undefined>(() => {
    // If breakDateStart exists AND is in the future, use it
    if (client.breakDateStart) {
      const breakDate = new Date(client.breakDateStart);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (breakDate >= today) {
        return breakDate;
      }
    }
    // Otherwise, use today
    return new Date();
  });
  const [startDateOpen, setStartDateOpen] = useState(false);

  const handlePauseSubscription = async () => {
    if (!pauseStartDate) {
      toast({
        title: t('error', { ns: 'common' }),
        description: t('pauseSubscription.startDateRequired', { ns: 'subscriptions' }),
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Validate: date must be today or in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (pauseStartDate < today) {
      toast({
        title: t('error', { ns: 'common' }),
        description: t('pauseSubscription.dateInPast', { ns: 'subscriptions' }),
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    const clientId = client.id;

    const success = await pauseSubscription(clientId, pauseStartDate.toISOString());

    if (success) {
      const clientName = `${client.firstName} ${client.lastName}`;
      toast({
        title: t('pauseSubscription.success', { ns: 'subscriptions' }),
        description: t('pauseSubscription.successMessage', { name: clientName, ns: 'subscriptions' }),
        duration: 3000,
      });
      fetchClientById(clientId);
      onOpenChange(false);
    } else {
      toast({
        title: t('error', { ns: 'common' }),
        description: t('pauseSubscription.error', { ns: 'subscriptions' }),
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleResumeSubscription = async () => {
    const success = await resumeSubscription(client.id);

    if (success) {
      const clientName = `${client.firstName} ${client.lastName}`;
      toast({
        title: t('resumeSubscription.success', { ns: 'subscriptions' }),
        description: t('resumeSubscription.successMessage', { name: clientName, ns: 'subscriptions' }),
        duration: 3000,
      });
      fetchClientById(client.id);
      onOpenChange(false);
    } else {
      toast({
        title: t('error', { ns: 'common' }),
        description: t('resumeSubscription.error', { ns: 'subscriptions' }),
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isPaused 
              ? t('resumeSubscription.title', { ns: 'subscriptions' }) 
              : t('pauseSubscription.title', { ns: 'subscriptions' })}
          </DialogTitle>
          <DialogDescription>
            {isPaused 
              ? t('resumeSubscription.description', { name: `${client.firstName} ${client.lastName}`, ns: 'subscriptions' }) 
              : t('pauseSubscription.description', { name: `${client.firstName} ${client.lastName}`, ns: 'subscriptions' })}
          </DialogDescription>
        </DialogHeader>

        {!isPaused &&(
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t('pauseSubscription.startDate', { ns: 'subscriptions' })}</Label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !pauseStartDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {pauseStartDate ? format(pauseStartDate, "PPP") : t('selectDate', { ns: 'subscriptions' })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={pauseStartDate}
                    onSelect={(date) => {
                      setPauseStartDate(date);
                      setStartDateOpen(false);
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        ) }

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('cancel', { ns: 'common' })}
          </Button>
          <Button 
            onClick={isPaused ? handleResumeSubscription : handlePauseSubscription} 
            disabled={isLoading || (!pauseStartDate && !isPaused)}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('processing', { ns: 'common' })}
              </>
            ) : isPaused ? (
              t('resumeSubscription.button', { ns: 'subscriptions' })
            ) : (
              t('pauseSubscription.button', { ns: 'subscriptions' })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}